import { useCallback, useMemo, useState } from "react";

import { type Cluster, type ClusterWithCloudConnect } from "@doitintl/cmp-models";
import { Box, Checkbox, CircularProgress, Divider, Stack, TextField, Typography } from "@mui/material";

import { useClustersWithCloudConnect } from "../../../../hooks";
import ClusterBulkSelectorItem from "./ClusterBulkSelectorItem";

interface ClusterBulkSelectorProps {
  projectId: string | null;
  filterAvailableClusters?: (cluster: Cluster) => boolean;
  onSelect: (clusters: Cluster["id"][]) => void;
  value?: Cluster["id"][] | null;
  allowPrivateClusters?: boolean;
}

const ClusterBulkSelector = ({
  projectId,
  onSelect,
  filterAvailableClusters,
  value = [],
  allowPrivateClusters = false,
}: ClusterBulkSelectorProps) => {
  const [search, setSearch] = useState("");
  const [clusters, clustersAreLoading] = useClustersWithCloudConnect();

  const clusterIsAvailable = useCallback(
    (cluster: ClusterWithCloudConnect) => allowPrivateClusters || !cluster.isPrivate,
    [allowPrivateClusters]
  );

  const { availableClusters, selectableClusters, filteredClusters, filteredSelectableClusters } = useMemo(() => {
    const availableClusters = clusters?.filter((c) =>
      filterAvailableClusters ? filterAvailableClusters(c) : c.projectId === projectId
    );
    const selectableClusters = availableClusters?.filter(clusterIsAvailable);
    const filteredClusters = availableClusters?.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
    const filteredSelectableClusters = filteredClusters?.filter(clusterIsAvailable);

    return { availableClusters, selectableClusters, filteredClusters, filteredSelectableClusters };
  }, [clusters, filterAvailableClusters, projectId, search, clusterIsAvailable]);

  const isSelectAllButtonDisabled = () => {
    const clustersToCheck = search ? filteredSelectableClusters : selectableClusters;
    return !clustersToCheck?.length;
  };

  const areAllClustersSelected = () => {
    if (!value) return false;
    const clustersToCheck = search ? filteredSelectableClusters : selectableClusters;
    return clustersToCheck?.every((c) => value.includes(c.id));
  };

  const getCurrentAvailableClusterCount = () =>
    search ? (filteredSelectableClusters?.length ?? 0) : (selectableClusters?.length ?? 0);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleSelect = (cluster: Cluster) => {
    if (!value) return;

    const newSelectedClusters = value.includes(cluster.id)
      ? value.filter((c) => c !== cluster.id)
      : [...value, cluster.id];

    onSelect(newSelectedClusters);
  };

  const handleSelectAll = () => {
    if (!value || isSelectAllButtonDisabled()) return;

    const clustersToSelect = search ? filteredSelectableClusters : selectableClusters;
    if (!clustersToSelect) return;

    const allFilteredSelected = clustersToSelect.every((c) => value.includes(c.id));

    const newSelectedClusters = allFilteredSelected
      ? value.filter((id) => !clustersToSelect.some((c) => c.id === id))
      : [...new Set([...value, ...clustersToSelect.map((c) => c.id)])];

    onSelect(newSelectedClusters);
  };

  if (clustersAreLoading) {
    return <CircularProgress />;
  }

  if (availableClusters?.length === 0) {
    return <Typography variant="body1">No clusters available</Typography>;
  }

  const selectAllButtonDisabled = isSelectAllButtonDisabled();

  return (
    <Stack spacing={2}>
      <TextField onChange={handleSearch} label="Cluster name" variant="outlined" fullWidth />
      <Stack>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              opacity: selectAllButtonDisabled ? 0.5 : 1,
              cursor: selectAllButtonDisabled ? "not-allowed" : "pointer",
            }}
          >
            <Checkbox
              onChange={handleSelectAll}
              checked={areAllClustersSelected()}
              disabled={selectAllButtonDisabled}
            />
            <Typography onClick={handleSelectAll} variant="body1">
              All clusters ({getCurrentAvailableClusterCount()})
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {value?.length} selected
          </Typography>
        </Box>
        <Divider />
        <Stack sx={{ overflowY: "auto", maxHeight: "40vh" }}>
          {filteredClusters?.map((cluster) => (
            <ClusterBulkSelectorItem
              key={cluster.id}
              cluster={cluster}
              allowPrivateClusters={allowPrivateClusters}
              selected={value?.includes(cluster.id) ?? false}
              onSelect={handleSelect}
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default ClusterBulkSelector;
